$checkboxWidth: 14px;
$checkboxHeight: 14px;
$borderRadius: 2px;

@mixin checkboxState($borderColor, $backgroundColor: 'initial', $pseudoSelector: '') {
  .checkbox--input#{$pseudoSelector} + .checkbox--label {
    &:before {
      border-color: #{$borderColor};
      background-color: #{$backgroundColor};
    }
  }
}

:host {
  --label-font-family: var(--font-family-secondary);
  --label-font-size: var(--body-size-large);
  display: inline-block;
  position: relative;
  height: auto;
  width: 100%;
  box-sizing: border-box;
}

.checkbox {
  width: 100%;

  // visually hide the default checkbox so we can add custom styles
  &--input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    border: 0;
    visibility: inherit;
    clip: rect(0px, 0px, 0px, 0px);
  }

  &--value {
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--main-titles-inputs-600);
  }

  &--label {
    font-size: var(--label-font-size);
    font-family: var(--label-font-family);
    line-height: 18px;
    padding-left: 26px;
    cursor: pointer;
    display: flex;
    user-select: none;

    // styles for the checkbox square
    &:before {
      content: '';
      position: absolute;
      left: 0;
      width: $checkboxWidth;
      height: $checkboxHeight;
      border-radius: $borderRadius;
      border: 2px solid;
      transition: all 0.3s ease-in-out;
    }
    // styles for the checkmark
    &:after {
      position: absolute;
      top: -2px;
      left: 4px;
      width: 10px;
      height: 5px;
      background: none;
      border-bottom: 2px solid var(--white-layout-301-level-0-elevation);
      border-left: 2px solid var(--white-layout-301-level-0-elevation);
      transform: scale(0) rotate(-45deg);
      transform-origin: right bottom;
      content: '';
    }
  }

  &.primary {
    @include checkboxState(var(--chart-300-icon-buttons));

    @include checkboxState(var(--blue-layout-501-focus), 'initial', ':focus-visible');

    &:hover {
      @include checkboxState(var(--main-titles-inputs-602));
    }
  }

  &.error {
    @include checkboxState(var(--red-layout-300-primary));

    @include checkboxState(var(--blue-layout-500-focus), 'initial', ':focus-visible');

    &:hover {
      @include checkboxState(var(--red-layout-201-hover));
    }
  }

  &:focus {
    border: none;
    outline: none;
  }
}

:host(:focus) {
  border: none;
  outline: none;
}

:host(:hover),
:host(.hover) {
  .checkbox {
    &.primary {
      @include checkboxState(var(--main-titles-inputs-602));
    }

    &.overlay {
      @include checkboxState(var(--chart-300-icon-buttons));
    }

    &.error {
      @include checkboxState(var(--red-layout-201-hover));
    }
  }
}

:host([checked]) {
  .checkbox {
    &--label {
      &:after {
        transform: scale(1) rotate(-45deg);
      }
    }

    &.primary {
      @include checkboxState(var(--blue-layout-400-primary), var(--blue-layout-400-primary));

      @include checkboxState(var(--blue-layout-501-focus), var(--blue-layout-501-focus), ':focus-visible');

      &:hover {
        @include checkboxState(transparent, var(--blue-layout-303-active));
      }
    }

    &.overlay {
      @include checkboxState(var(--white-layout-300-primary), var(--white-layout-300-primary));

      @include checkboxState(var(--blue-layout-501-focus), var(--blue-layout-501-focus), ':focus-visible');

      &:hover {
        @include checkboxState(var(--white-layout-010-hover-primary), var(--white-layout-010-hover-primary));
      }

      .checkbox--label {
        &:after {
          transform: scale(1) rotate(-45deg);
          border-bottom: 2px solid var(--blue-layout-301-active);
          border-left: 2px solid var(--blue-layout-301-active);
        }
      }
    }

    &.error {
      @include checkboxState(var(--red-layout-300-primary), var(--red-layout-300-primary));

      @include checkboxState(var(--blue-layout-501-focus), var(--blue-layout-501-focus), ':focus-visible');

      &:hover {
        @include checkboxState(transparent, var(--red-layout-201-hover));
      }
    }
  }
}

:host([checked]:hover),
:host([checked].hover) {
  .checkbox {
    &.primary {
      @include checkboxState(transparent, var(--blue-layout-303-active));
    }

    &.error {
      @include checkboxState(transparent, var(--red-layout-201-hover));
    }
  }
}

:host([checked]),
:host([indeterminate]) {
  .checkbox {
    &:focus {
      &.primary,
      &.error {
        @include checkboxState(var(--blue-layout-501-focus), var(--blue-layout-501-focus));
      }
    }
  }
}

:host(:active) {
  .checkbox {
    &.primary {
      @include checkboxState(var(--blue-layout-400-primary));
    }

    &.overlay {
      @include checkboxState(var(--black-layout-700-primary));
    }

    &.error {
      @include checkboxState(var(--red-layout-300-primary));
    }
  }
}

:host([checked]:active) {
  .checkbox {
    &.primary {
      @include checkboxState(var(--blue-layout-400-primary), var(--blue-layout-400-primary));
    }

    &.overlay {
      @include checkboxState(var(--black-layout-700-primary), var(--black-layout-700-primary));
    }

    &.error {
      @include checkboxState(var(--red-layout-300-primary), var(--red-layout-300-primary));
    }
  }
}

:host([readonly]),
:host([disabled]) {
  pointer-events: none;

  .checkbox {
    &.primary,
    &.error {
      @include checkboxState(var(--grey-layout-202-disabled-icons));

      @include checkboxState(var(--blue-layout-501-focus), 'initial', ':focus-visible');
    }
  }
}

:host([readonly]:not([checked]):not([indeterminate])),
:host([disabled]:not([checked]):not([indeterminate])) {
  .checkbox--value {
    color: var(--grey-layout-200-disabled);
  }
}

:host([indeterminate]) {
  .checkbox {
    &.primary,
    &.error {
      .checkbox--label {
        &:after {
          top: 2.5px;
          width: 10px;
          border-bottom: 2px solid var(--white-layout-301-level-0-elevation);
          border-left: 0 solid var(--white-layout-301-level-0-elevation);
          transform: scale(1) rotate(0deg);
        }
      }
    }

    &.primary {
      @include checkboxState(
        var(--blue-layout-400-primary),
        var(--blue-layout-400-primary)
      );
    }

    &.error {
      @include checkboxState(var(--red-layout-300-primary), var(--red-layout-300-primary));
    }

    &.primary,
    &.error {
      @include checkboxState(var(--blue-layout-501-focus), var(--blue-layout-501-focus), ':focus-visible');
    }

    &.primary:hover {
      @include checkboxState(transparent, var(--blue-layout-303-active));
    }

    &.error:hover {
      @include checkboxState(transparent, var(--red-layout-201-hover));
    }

    &.primary:active {
      @include checkboxState(
        var(--blue-layout-400-primary),
        var(--blue-layout-400-primary)
      );
    }

    &.error:active {
      @include checkboxState(var(--red-layout-300-primary), var(--red-layout-300-primary));
    }
  }
}

:host([readonly][checked]),
:host([readonly][indeterminate]),
:host([disabled][checked]),
:host([disabled][indeterminate]) {
  .checkbox {
    &.primary,
    &.error {
      @include checkboxState(transparent, var(--grey-layout-200-disabled));

      @include checkboxState(var(--blue-layout-501-focus), var(--blue-layout-501-focus), ':focus-visible');
    }
  }
}

:host(.full-checkbox) {
  display: flex;

  .checkbox {
    position: relative;
    transition: all 0.3s ease;

    &:before {
      content: '';
      width: calc(100% + 16px);
      height: 100%;
      position: absolute;
      z-index: -1;
      left: calc(-1 * var(--spacer));
      top: 0;
      background-color: var(--chart-051-hover);
      opacity: 0;
      transition: all 0.3s ease;
    }

    &:focus-within {
      &:before {
        background-color: var(--blue-layout-302-active-focus);
        opacity: 1;
        border: 1px solid var(--blue-layout-501-focus);
        border-left: none;
        border-right: none;
      }
    }

    &--label {
      padding-top: 7px;
      padding-bottom: 7px;

      &:before {
        //fixes the alignment when interracting with gz-font-size-changer
        top: calc(50% - 9px);
      }
      &:after {
        top: calc(50% - 11px);
      }
    }

    &:hover {
      &:before {
        opacity: 1;
      }
    }

    &:active {
      &:before {
        background-color: var(--chart-102-pressed);
        border: unset;
      }
    }
  }
}

:host([indeterminate].full-checkbox) {
  .checkbox {
    &--label {
      &:after {
        top: calc(50% - 6px) !important;
      }
    }
  }
}
